import '../globalWindowPatches'
import {
  type WorkspaceDomainAttributes,
  isCertified,
  isPendingOrRetrying,
  WorkspaceDomain,
} from 'api/clients/WorkspaceDomainClientUtils'
import { REACT_APP_FRONTEND_HOSTNAME, REACT_APP_FRONTEND_ORIGIN, REACT_APP_FRONTEND_PROTOCOL } from 'env'
import { getLocation } from 'hooks/useHistory'
import { WORKSPACE_URL_JOIN } from 'paths'
import { matchPath } from 'react-router-dom'
import { WORKSPACE_INVITE_TOKEN_PARAM } from 'screens/LoginOrSignup/constants'
import { Workspace } from 'types'
import { ALMANAC_CORE_WORKSPACE, ALMANAC_CORE_WORKSPACE_ID } from 'utils/constants'

/**
 * Returns true if the workspace is on the default domain.
 *
 * A workspace is in the default domain if
 * - It doesn't have any subdomain configured
 * - It has a subdomain configured
 *
 * @param ws
 * @returns
 */
const onDefaultDomain = (ws: Workspace) => {
  return !ws.workspace_domain || ws.workspace_domain.subdomain
}

export const excludeCore = (ws: Workspace) => {
  return ws.id !== ALMANAC_CORE_WORKSPACE_ID
}

export const isValidDomain = (ws: Workspace) =>
  onDefaultDomain(ws) || isCertified(ws.workspace_domain) || isPendingOrRetrying(ws.workspace_domain)

/**
 * Returns a function that asses if the workspace passed as an iteratee is on a valid domain.
 *
 * Valid domains are the default domain or a certified domain. Pending domains we still want
 * to flag them as valid as they could be in the process on being validated. This means
 * that we still want to redirect users to their workspace but not under their custom
 * domain.
 *
 * @param id
 * @returns function
 */
export const onValidDomain = (id?: number | null) => {
  return (ws: Workspace) => (!id || ws.id === id) && isValidDomain(ws)
}

const onMatchingDomain = (domain: WorkspaceDomainAttributes) => {
  return (ws: Workspace) =>
    ws.workspace_domain &&
    ws.workspace_domain.subdomain === domain.subdomain &&
    ws.workspace_domain.name === domain.name
}

const withCustomDomain = (domain: string) => {
  return (ws: Workspace) => {
    return (
      ws.workspace_domain &&
      (isCertified(ws.workspace_domain) || isPendingOrRetrying(ws.workspace_domain)) &&
      !ws.workspace_domain.subdomain &&
      ws.workspace_domain.name === domain
    )
  }
}

/**
 * This function's purpose is called when we need to redirect to a certain workspace after login/signup.
 * We need to ensure that the workspace to redirect to is a valid one given a possible customDomain
 *
 * If there is a `customDomain`` param, we need to return the workspace corresponding to that domain
 * (full domain, for subdomains, `customDomain` would be undefined).
 * If none can be found, return undefined.
 *
 * If there is no `customDomain` param, we need to return the workspace having ID == currentWorkspaceId if it's on a valid domain.
 * If it's not on a valid domain, try to find any other workspace on a valid domain that is not core.
 *
 * If currentWorkspaceId is undefined, try to find any workspace on a valid domain that is not core.
 *
 * If nothing but core is found, return core.
 *
 * If none can't be found, return undefined.
 *
 * @param workspaces: Workspace[]
 * @param currentWorkspaceId?: number | null
 * @param customDomain?: string | undefined
 * @returns workspace | undefined
 */
export const workspaceAfterLogin = ({
  workspaces,
  currentWorkspaceId,
  customDomain,
  forceDefaultWorkspace,
}: {
  workspaces: Workspace[]
  currentWorkspaceId?: number | null
  customDomain?: string
  forceDefaultWorkspace?: boolean
}): Workspace | undefined => {
  const isPayingWorkspace = (workspace: Workspace) => workspace.paying

  const getPayingWorkspace = () => workspaces.find(isPayingWorkspace)

  // At login we can ask to remain at almanac.io
  if (forceDefaultWorkspace) {
    const nonCoreAtDefaultDomain = workspaces.filter(excludeCore).find(onDefaultDomain)
    if (nonCoreAtDefaultDomain && isPayingWorkspace(nonCoreAtDefaultDomain)) return nonCoreAtDefaultDomain
    if (nonCoreAtDefaultDomain && !isPayingWorkspace(nonCoreAtDefaultDomain)) {
      const payingWorkspace = getPayingWorkspace()
      if (payingWorkspace) return payingWorkspace
    }
    return ALMANAC_CORE_WORKSPACE
  }

  // A custom domain param sets precedence over all else
  const customDomainWorkspace =
    customDomain && !forceDefaultWorkspace ? workspaces.find(withCustomDomain(customDomain)) : undefined
  if (customDomainWorkspace && isPayingWorkspace(customDomainWorkspace)) return customDomainWorkspace
  if (customDomainWorkspace && !isPayingWorkspace(customDomainWorkspace)) {
    const payingWorkspace = getPayingWorkspace()
    if (payingWorkspace) return payingWorkspace
  }

  // If the last_workspace_id is set, try to find that workspace (core included)
  const currentWorkspace = workspaces.find(onValidDomain(currentWorkspaceId))
  if (currentWorkspaceId && currentWorkspace && isPayingWorkspace(currentWorkspace)) return currentWorkspace
  if (currentWorkspaceId && currentWorkspace && !isPayingWorkspace(currentWorkspace)) {
    const payingWorkspace = getPayingWorkspace()
    if (payingWorkspace) return payingWorkspace
  }

  // Email registering users or clear sessions, with no determined workspace yet
  const firstNonCore = workspaces.filter(excludeCore).find(onValidDomain())
  if (firstNonCore && isPayingWorkspace(firstNonCore)) return firstNonCore
  if (firstNonCore && !isPayingWorkspace(firstNonCore)) {
    const payingWorkspace = getPayingWorkspace()
    if (payingWorkspace) return payingWorkspace
  }

  // Core
  return ALMANAC_CORE_WORKSPACE
}

/**
 * This function's purpose is called when we need to redirect to a certain workspace. We need
 * to ensure that the workspace to redirect to is a valid one given the current hostname of the
 * user.
 *
 * If the current hostname is NOT the default hostname, ignore the given currentWorkspaceId
 * and try to find among the workspaces a workspace corresponding to the current (sub)domain.
 * If none can be found, return undefined.
 *
 * If the current hostname is the default hostname, try to find among the workspaces the workspace
 * with ID == currentWorkspaceId that has a valid domain (default, subdomain or custom domain).
 * If this workspace does not exist, try to find another workspace that has no custom subdomain or domain,
 * and that isn't the core workspace. If none can't be found, return undefined.
 *
 * @param workspaces: Workspace[]
 * @param currentWorkspaceId?: number | null
 * @returns workspace | undefined
 */
export const workspaceToRedirectTo = ({
  workspaces,
  currentWorkspaceId,
}: {
  workspaces: Workspace[]
  currentWorkspaceId?: number | null
}): Workspace | undefined => {
  const workspaceDomainAttributes = workspaceDomainAttributesFromHostname(window.getHostname())

  if (workspaceDomainAttributes) {
    const domainMatchingWorkspace = workspaces.find(onMatchingDomain(workspaceDomainAttributes))
    if (domainMatchingWorkspace) return domainMatchingWorkspace
  }

  const currentWorkspace = workspaces.filter(excludeCore).find(onValidDomain(currentWorkspaceId))
  if (currentWorkspace) return currentWorkspace

  return workspaces.filter(excludeCore).find(onValidDomain())
}

export function getWorkspaceOrigin(workspaceDomain?: WorkspaceDomain | null): string {
  if (!workspaceDomain) return REACT_APP_FRONTEND_ORIGIN
  if (workspaceDomain.subdomain) {
    return `${REACT_APP_FRONTEND_PROTOCOL}//${buildReaderSubdomainFromName(workspaceDomain.name)}`
  }
  if (isCertified(workspaceDomain) || isPendingOrRetrying(workspaceDomain)) {
    return `${REACT_APP_FRONTEND_PROTOCOL}//${workspaceDomain.name}`
  }
  return REACT_APP_FRONTEND_ORIGIN
}

export const buildReaderSubdomainFromName = (name: string): string => {
  return `${name.toLowerCase()}.${REACT_APP_FRONTEND_HOSTNAME}`
}

export const redirectToFrontendDomainIfNecessary = ({
  pathnameOvewrite,
  searchParamsOvewrite,
}: {
  pathnameOvewrite?: string
  searchParamsOvewrite?: URLSearchParams
} = {}) => {
  const pathname = pathnameOvewrite ?? getLocation().pathname
  const searchParams = searchParamsOvewrite ?? new URLSearchParams(getLocation().search)
  const workspaceDomainAttributes = workspaceDomainAttributesFromHostname(window.getHostname())

  // if we are not on almanac.io
  if (workspaceDomainAttributes) {
    // if on a subdomain, just redirect, we have a cookie which works on subdomains as well
    if (workspaceDomainAttributes.subdomain) {
      window.redirectToHostname(REACT_APP_FRONTEND_HOSTNAME, pathname, searchParams)
      return
    }

    const invitePath = matchPath<{ slug: string }>(pathname, { strict: true, path: WORKSPACE_URL_JOIN })
    if (invitePath != null) {
      searchParams.set(WORKSPACE_INVITE_TOKEN_PARAM, invitePath.params.slug)
    }
    window.redirectToHostname(REACT_APP_FRONTEND_HOSTNAME, pathname, searchParams)
  }
}

export const workspaceDomainAttributesFromHostname = (hostname: string): WorkspaceDomainAttributes | null => {
  const subdomainMatch = hostname.match(new RegExp(`(.+)\\.${REACT_APP_FRONTEND_HOSTNAME.replace('.', '\\.')}$`))

  if (hostname === REACT_APP_FRONTEND_HOSTNAME) return null
  if (subdomainMatch) return { name: subdomainMatch[1], subdomain: true }
  return { name: hostname, subdomain: false }
}

export const redirectToSubdomainIfNecessary = (workspaceDomain: WorkspaceDomainAttributes) => {
  const readerSubdomain = buildReaderSubdomainFromName(workspaceDomain.name)

  if (workspaceDomain.subdomain && window.getHostname().toLowerCase() !== readerSubdomain) {
    const searchParams = new URLSearchParams(getLocation().search)
    window.redirectToHostname(readerSubdomain, getLocation().pathname, searchParams)
  }
}

export const redirectToCustomDomainIfNecessary = (workspaceDomain: WorkspaceDomainAttributes) => {
  if (!workspaceDomain.subdomain && window.getHostname().toLowerCase() !== workspaceDomain.name.toLowerCase()) {
    const searchParams = new URLSearchParams(getLocation().search)
    window.redirectToHostname(workspaceDomain.name, getLocation().pathname, searchParams)
  }
}

export const redirectToWorkspaceDomainIfNecessary = (workspaceDomain: WorkspaceDomainAttributes) => {
  redirectToCustomDomainIfNecessary(workspaceDomain)
  redirectToSubdomainIfNecessary(workspaceDomain)
}
